/**
 * Mantenimiento. Kanban de 3 columnas.
 *
 * Las columnas son el enum `maintenance_issues.status`, y el distintivo y la etiqueta de tiempo
 * de cada tarjeta los resuelve la API según el estado (prioridad si está abierta, oficio del
 * proveedor si está en curso, «Resuelta» si lo está).
 */
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Badge } from '../components/Badge';
import { ErrorState } from '../components/EmptyState';
import { SkeletonPageHeader, Skeleton } from '../components/Skeleton';
import { formatEur } from '../lib/format';
import styles from './MaintenancePage.module.css';

export function MaintenancePage() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.maintenance(),
    queryFn: () => api.maintenanceIssues(),
  });

  const header = (
    <PageHeader
      title="Mantenimiento"
      subtitle="Incidencias, tareas y proveedores en un solo tablero."
      actions={
        <Button variant="primary" phaseTwo>
          <Icon name="plus" size={17} strokeWidth={2.4} />
          Nueva incidencia
        </Button>
      }
    />
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <div className={styles.board}>
          {[0, 1, 2].map((column) => (
            <div key={column} className={styles.column}>
              <Skeleton width="40%" height={13} />
              <Skeleton height={112} radius={8} />
              <Skeleton height={112} radius={8} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}
      <div className={styles.board}>
        {data.columns.map((column) => (
          <div key={column.status} className={styles.column}>
            <div className={styles.columnTitle}>
              {column.label} · {column.count}
            </div>

            {column.issues.length === 0 ? (
              <div className={styles.emptyColumn}>Sin incidencias en esta columna</div>
            ) : (
              column.issues.map((issue) => (
                <Card
                  key={issue.id}
                  variant="xs"
                  className={
                    issue.status === 'resuelta'
                      ? `${styles.card} ${styles.cardResolved}`
                      : styles.card
                  }
                >
                  <div className={styles.cardHead}>
                    <Badge tone={issue.badge.tone} size="kanban">
                      {issue.badge.label}
                    </Badge>
                    <span className={styles.time}>{issue.timeLabel}</span>
                  </div>
                  <div className={styles.title}>{issue.title}</div>
                  <div className={styles.placement}>{issue.placement}</div>
                  {issue.cost ? (
                    <div className={styles.cost}>Coste: {formatEur(issue.cost)}</div>
                  ) : null}
                </Card>
              ))
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
